import vTextarea from '@/ui/controls/vTextarea'
import UploadSingle from '@/ui/controls/UploadSingle'
import vCheckbox from '@/ui/controls/vCheckbox'
import DateInput from '@/ui/controls/DateInput'
import vInput from '@/ui/controls/vInput'
import vAutocomplete from '@/ui/controls/Autocomplete'
import Multiselect from '@/ui/controls/MultiSelect'

import { ReqCountriesGQL, CategoriesGQL, CountriesGQL, CitiesGQL } from '@/graphql/queries'

import UsaTax from './UsaTax'
import rewardTypes from './rewardTypes'
import rewardFix from './rewardFix'
import rewardPercent from './rewardPercent'
import addShop from './addShop'
import documentTypes from './documentTypes'
import recruitedUser from './recruitedUser'
import documentsAccept from './documentsAccept'
import rulesAccept from './rulesAccept'
import greeting from './greeting'
import w9warning from './w9warning'
import goldShopConditions from './goldShopConditions'
import fillByEgrul from './fillByEgrul'

import validate from '@/mixins/validate'
import profile from '@/mixins/profile'
import getTranslate from '@/mixins/getTranslate'
import MexicaTax from '@/components/Profile/MexicaTax'
import taxDataWarn from '@/components/Profile/taxDataWarn'
import adAgree from '@/components/Profile/adAgree'

const fileExtensions = ['jpg', 'jpeg', 'gif', 'png', 'txt', 'doc', 'docx', 'rtf', 'tif', 'tiff', 'psd', 'svg', 'ai', 'pdf']

export default {
  mixins: [validate, profile, getTranslate],

  apollo: {
    reqCountries: {
      query: ReqCountriesGQL,
      update: data => data.commonData.ReqCountries,
      skip () { return this.skipFetchCommonData }
    },

    categories: {
      query: CategoriesGQL,
      update: data => data.commonData.Categories,
      skip () { return this.skipFetchCommonData }
    },

    countries: {
      query: CountriesGQL,
      update: data => data.commonData.Countries,
      skip () { return this.skipFetchCommonData }
    },

    cities: {
      query: CitiesGQL,
      variables () {
        return {
          cityName: '',
          countryIds: this.countryIds
        }
      },
      update: data => data.commonData.Cities,
      skip () {
        return !this.countryIds.length || this.skipFetchCommonData
      }
    }
  },

  methods: {
    /**
     * @return {boolean}
     * @param {string} keys
     */
    hasFieldVal (...keys) {
      let count = 0
      let has = 0
      for (const key of keys) {
        count++
        if (typeof this.form[key] === 'string' || Array.isArray(this.form[key])) {
          return this.form[key] && this.form[key].length
        }

        if (typeof this.form[key] === 'number') {
          return this.form[key] && this.form[key] !== null
        }

        if (this.form[key]) {
          has++
        }
      }
      return count === has
    },

    fieldHasValue (key, value) {
      return this.form[key] === value
    },

    /**
     * @typedef ShopListItem
     * @property {string} ShopListItem.name
     * @property {string} ShopListItem.address
     * @property {number} ShopListItem.lat
     * @property {number} ShopListItem.lng
     */

    /**
     * @param {ShopListItem[]} val
     * @returns {string[] | void}
     */
    validateShopListMap (val) {
      const errors = []
      val.forEach((item) => {
        if (!item.name && item.address) {
          errors.push(this.$t('errors.code.shopNameRequired'))
        } else if (item.name && !item.address) {
          errors.push(this.$t('errors.code.shopAddressRequired'))
        } else if (!item.name) {
          errors.push(this.$t('errors.code.shopNameRequired'))
        } else if (!item.address) {
          errors.push(this.$t('errors.code.shopAddressRequired'))
        } else {
          errors.push(null)
        }
      })

      let hasErrors = false
      errors.forEach(e => {
        if (e) {
          hasErrors = true
        }
      })

      if (!hasErrors) {
        return
      }

      if (errors.length > 0) return errors
    },
    /**
     * @param {boolean} val
     * @return {string | void}
     */
    validateOnlineOffline (val) {
      if (!this.reqCountry.USA && !this.reqCountry.MEXICO) return
      if (!this.hasFieldVal('online') && !this.hasFieldVal('offline')) {
        return this.$t('errors.code.minimumOneTypeMustBeSpecified')
      }
    }
  },

  computed: {
    skipFetchCommonData () {
      return typeof this.computedShow !== 'undefined' && this.computedShow === false
    },
    computedCategories () {
      if (!this.categories) {
        return []
      }

      return this.categories.map(cat => {
        const tr = this.getTranslate(cat.translationList, 'locale')
        return {
          ...tr,
          id: cat.id
        }
      })
    },

    computedCountries () {
      if (!this.countries) {
        return []
      }

      return this.countries
    },

    countryIds () {
      if (!this.form.countryList) {
        return []
      }

      return this.form.countryList.map(c => c.id)
    },

    reqCountry () {
      return {
        /**
         * @return {Boolean}
         */
        USA: this.form && this.form.reqCountry && this.form.reqCountry && this.form.reqCountry.id === 6252001,
        RUSSIA: this.form && this.form.reqCountry && this.form.reqCountry && this.form.reqCountry.id === 2017370,
        BELARUS: this.form && this.form.reqCountry && this.form.reqCountry && this.form.reqCountry.id === 630336,
        KAZAKHSTAN: this.form && this.form.reqCountry && this.form.reqCountry && this.form.reqCountry.id === 1522867,
        UKRAINE: this.form && this.form.reqCountry && this.form.reqCountry && this.form.reqCountry.id === 690791,
        MOLDOVA: this.form && this.form.reqCountry && this.form.reqCountry && this.form.reqCountry.id === 617790,
        MEXICO: this.form && this.form.reqCountry && this.form.reqCountry && this.form.reqCountry.id === 3996063
      }
    },

    forceDisable () {
      return this.profile.active || !!(!this.profile.active && this.profile.status && this.profile.status.match(/VERIFICATION|READY|DEACTIVATED|OUT_PLATFORM/g))
    },

    formFields () {
      return {
        entitySection: {
          name: this.$i18n.t('components.Profile.formFields.registrationData'),
          /**
           * @type {Function | boolean}
           * @returns {boolean}
           */
          show: () => true,
          fields: {
            reqCountry: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.reqCountry'),
                items: this.reqCountries || [],
                itemText: 'name',
                valueField: 'id',
                emitObject: true,
                disabled: !this.reqCountries || !this.newProfile
                // disabled: !this.reqCountries || this.forceDisable
              },
              component: vAutocomplete,
              watchEvent: 'input',
              required: true
            },
            rulesAccept: {
              default: false,
              component: rulesAccept,
              required: true,
              show: this.hasFieldVal('reqCountry') && (this.reqCountry.USA || this.reqCountry.MEXICO) && this.newProfile,
              props: {
                disabled: this.forceDisable,
                reqCountry: this.reqCountry
              }
            },
            greeting: {
              component: greeting,
              optionalDesc: false,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.MEXICO && this.newProfile
            },

            // RUSSIA ONLY
            reqInn: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.registerData'),
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.INN') + '*',
                typeName: 'number',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: fillByEgrul,
              required: true,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.RUSSIA
            },

            // Moldova ONLY
            reqIdno: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.registerData'),
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.IDNO') + '*',
                typeName: 'number',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              required: true,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.MOLDOVA
            },

            reqLegalName: {
              default: null,
              title: this.reqCountry.RUSSIA || this.reqCountry.MOLDOVA ? null : this.$i18n.t('components.Profile.formFields.registerData'),
              props: {
                placeholder: this.reqCountry.USA || this.reqCountry.MEXICO ? this.$i18n.t('components.Profile.formFields.title') + '*' : this.$i18n.t('components.Profile.formFields.companyLegalName') + '*',
                label: this.reqCountry.USA || this.reqCountry.MEXICO ? this.$i18n.t('components.Profile.formFields.companyName') + '*' : this.$i18n.t('components.Profile.formFields.companyLegalName') + '*',
                disabled: ((this.reqCountry.USA || this.reqCountry.MEXICO) ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry')) || this.forceDisable
              },
              component: vInput,
              required: true
            },

            // BELARUS ONLY
            reqUpn: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.UPN') + '*',
                typeName: 'number',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.BELARUS,
              validate: (val) => {
                return this.validate(val).maxLength(20)
              },
              required: true
            },

            // KAZAKHSTAN ONLY
            reqBsn: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.BSN_BIN') + '*',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.KAZAKHSTAN,
              validate: (val) => {
                return this.validate(val).maxLength(20)
              },
              required: true
            },
            reqIik: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.IIC') + '*',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.KAZAKHSTAN,
              validate: (val) => {
                return this.validate(val).maxLength(20)
              },
              required: true
            },

            // RUSSIA ONLY
            reqKpp: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.checkpoint') + '*',
                typeName: 'number',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              required: true,
              component: vInput,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.RUSSIA
            },
            reqOgrn: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.OGRN') + '*',
                typeName: 'number',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              required: true,
              component: vInput,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.RUSSIA
            },

            // UKRAINE ONLY
            reqEdrpou: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.EDRPOUCode') + '*',
                typeName: 'number',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              required: true,
              component: vInput,
              validate: (val) => {
                return this.validate(val).maxLength(20)
              },
              show: this.hasFieldVal('reqCountry') && this.reqCountry.UKRAINE
            },
            reqIpn: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.IIT') + '*',
                typeName: 'number',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              required: true,
              validate: (val) => {
                return this.validate(val).maxLength(20)
              },
              show: this.hasFieldVal('reqCountry') && this.reqCountry.UKRAINE
            },
            reqCscp: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.singleInstallmentPayerCode') + '*',
                typeName: 'number*',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              required: true,
              validate: (val) => {
                return this.validate(val).maxLength(50)
              },
              component: vInput,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.UKRAINE
            },
            reqMfo: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.MFO') + '*',
                typeName: 'number',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              required: true,
              validate: (val) => {
                return this.validate(val).maxLength(20)
              },
              component: vInput,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.UKRAINE
            },

            // USA AND MEXICO ONLY
            reqBusinessName: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.unrecordedCompanyName'),
                label: this.$i18n.t('components.Profile.formFields.unrecordedOrganizationName'),
                disabled: ((this.reqCountry.USA || this.reqCountry.MEXICO) ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry')) || this.forceDisable
              },
              component: vInput,
              show: this.hasFieldVal('reqCountry') && (this.reqCountry.USA || this.reqCountry.MEXICO)
            },
            reqClassification: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.checkTaxClassification') + ':',
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.taxClassification') + '*',
                // typeName: 'number',
                disabled: (this.reqCountry.USA ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry')) || this.forceDisable
              },
              required: true,
              component: UsaTax,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.USA
            },
            reqClassificationMX: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.checkTaxClassification') + ':',
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.taxClassification') + '*',
                // typeName: 'number',
                disabled: (this.reqCountry.MEXICO ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry')) || this.forceDisable
              },
              required: true,
              component: MexicaTax,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.MEXICO
            },

            w9warning: {
              component: w9warning,
              optionalDesc: false,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.USA,
              props: {
                disabled: this.forceDisable
              }
            },

            taxDataWarn: {
              component: taxDataWarn,
              optionalDesc: false,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.MEXICO,
              props: {
                disabled: this.forceDisable
              }
            },

            reqCompanyRegistrationDate: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.companyRegistrationDate') + (!this.reqCountry.MOLDOVA && !this.reqCountry.USA && !this.reqCountry.MEXICO ? '*' : ''),
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              required: () => !this.reqCountry.MOLDOVA && !this.reqCountry.USA && !this.reqCountry.MEXICO,
              component: DateInput,
              show: this.hasFieldVal('reqCountry') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            reqLegalAddress: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.legalAddress') + '*',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              required: true,
              show: this.hasFieldVal('reqCountry') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },

            // RUSSIA ONLY
            reqOkved: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.OKVED') + '*',
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              required: true,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.RUSSIA
            },

            reqCompanyDirector: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.companyDirector') + (!this.reqCountry.MOLDOVA && !this.reqCountry.USA && !this.reqCountry.MEXICO ? '*' : ''),
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              required: !this.reqCountry.MOLDOVA && !this.reqCountry.USA && !this.reqCountry.MEXICO,
              show: this.hasFieldVal('reqCountry') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            reqPostAddress: {
              default: null,
              props: {
                placeholder: (this.reqCountry.USA || this.reqCountry.MEXICO) ? this.$i18n.t('components.Profile.formFields.cityStateZipCode') + '*' : this.$i18n.t('components.Profile.formFields.mailingAddress') + '*',
                disabled: ((this.reqCountry.USA || this.reqCountry.MEXICO) ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry')) || this.forceDisable
              },
              component: vInput,
              required: true
            },
            reqActualAddress: {
              default: null,
              props: {
                placeholder: ((this.reqCountry.USA || this.reqCountry.MEXICO) ? this.$i18n.t('components.Profile.formFields.address') : this.$i18n.t('components.Profile.formFields.actualAddress')) + '*',
                disabled: ((this.reqCountry.USA || this.reqCountry.MEXICO) ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry')) || this.forceDisable
              },
              required: true,
              component: vInput
            },
            reqAccountantGeneral: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.chiefAccountant'),
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              show: this.hasFieldVal('reqCountry') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },

            // USA ONLY
            reqTin: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.TIN') + '*',
                label: 'TIN',
                disabled: ((this.reqCountry.USA || this.reqCountry.MEXICO) ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry')) || this.forceDisable
              },
              component: vInput,
              required: true,
              show: this.hasFieldVal('reqCountry') && (this.reqCountry.USA || this.reqCountry.MEXICO)
            },

            bankCheckingAccount: {
              title: this.$i18n.t('components.Profile.formFields.bankDetails'),
              default: null,
              props: {
                placeholder: this.$t('components.Profile.formFields.checkingAccount') + (!this.reqCountry.USA && !this.reqCountry.MOLDOVA && !this.reqCountry.MEXICO ? '*' : ''),
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              required: () => !this.reqCountry.USA && !this.reqCountry.MOLDOVA && !this.reqCountry.MEXICO,
              show: this.hasFieldVal('reqCountry') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            bankName: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.beneficiaryBankName') + (!this.reqCountry.USA && !this.reqCountry.MOLDOVA && !this.reqCountry.MEXICO ? '*' : ''),
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              required: () => !this.reqCountry.USA && !this.reqCountry.MOLDOVA && !this.reqCountry.MEXICO,
              component: vInput,
              show: this.hasFieldVal('reqCountry') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            bankCorrAccount: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.correspondentAccount') + (!this.reqCountry.USA && !this.reqCountry.MOLDOVA && !this.reqCountry.MEXICO ? '*' : ''),
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              component: vInput,
              required: () => !this.reqCountry.USA && !this.reqCountry.MOLDOVA && !this.reqCountry.MEXICO,
              show: this.hasFieldVal('reqCountry') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            bankBik: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.BIK') + (!this.reqCountry.USA && !this.reqCountry.MOLDOVA && !this.reqCountry.MEXICO ? '*' : ''),
                disabled: !this.hasFieldVal('reqCountry') || this.forceDisable
              },
              required: () => !this.reqCountry.USA && !this.reqCountry.MOLDOVA && !this.reqCountry.MEXICO,
              component: vInput,
              show: this.hasFieldVal('reqCountry') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },

            reqContactPhone: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.contactInformation'),
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.contactNumber') + '*',
                disabled: ((this.reqCountry.USA || this.reqCountry.MEXICO) ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry')) || this.forceDisable,
                typeName: 'tel'
              },
              component: vInput,
              required: true
            },
            reqTechnicalServicePhone: {
              default: null,
              props: {
                placeholder: (this.reqCountry.USA || this.reqCountry.MEXICO) ? this.$i18n.t('components.Profile.formFields.technicalServicePhone') + '*' : this.$i18n.t('components.Profile.formFields.technicalServicePhone'),
                disabled: ((this.reqCountry.USA || this.reqCountry.MEXICO) ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry')) || this.forceDisable
              },
              required: this.reqCountry.USA || this.reqCountry.MEXICO,
              component: vInput
            }
          }
        },

        profileSection: {
          name: this.$i18n.t('components.Profile.formFields.storeProfile') + '*',
          fields: {
            recruitedUser: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.referringPartner'),
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.invitingPartnerLogin'),
                disabled: this.forceDisable || !this.newProfile
              },
              component: recruitedUser
            },

            recruitedLoginApprove: {
              default: false,
              props: {
                label: this.hasFieldVal('recruitedUser') ? this.$i18n.t('components.Profile.formFields.loginCorrect') + '*' : this.$i18n.t('components.Profile.formFields.loginCorrect'),
                type: 'boxed',
                val: 'loginApprove',
                disabled: !this.hasFieldVal('recruitedUser') || this.forceDisable
              },
              required: () => this.hasFieldVal('recruitedUser'),
              show: this.newProfile,
              component: vCheckbox
            },

            email: {
              title: this.$i18n.t('components.Profile.formFields.aboutStore'),
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.emailAddress') + '*',
                disabled: this.forceDisable
              },
              validate: (val) => {
                return [this.validate(val).isEmail()]
              },
              component: vInput,
              required: true
            },

            marketingName: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.shopMarketingName') + '*',
                disabled: this.forceDisable
              },
              validate: (val) => {
                return [this.validate(val).minLength(2), this.validate(val).maxLength(250)]
              },
              component: vInput,
              required: true
            },

            orderCategory: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.shopCategory') + ((this.reqCountry.USA || this.reqCountry.MEXICO) ? '*' : ''),
                items: this.computedCategories,
                itemText: 'name',
                valueField: 'id',
                emitObject: true,
                disabled: this.forceDisable
              },
              required: this.reqCountry.USA || this.reqCountry.MEXICO,
              component: vAutocomplete
            },

            adAgree: {
              component: adAgree,
              optionalDesc: false,
              show: this.hasFieldVal('reqCountry') && this.reqCountry.MEXICO,
              props: {
                disabled: this.forceDisable
              }
            },

            rewardType: {
              default: 'percent',
              title: this.$i18n.t('components.Profile.formFields.remunerationAmount'),
              props: {
                disabled: this.forceDisable
              },
              component: rewardTypes
            },

            rewardPercent: {
              default: null,
              component: rewardPercent,
              props: {
                disabled: this.forceDisable
              },
              validate: (val) => {
                return [this.validate(val).fractionDigitsMax(2)]
              },
              show: this.fieldHasValue('rewardType', 'percent')
            },

            rewardAmount: {
              default: null,
              props: {
                disabled: this.forceDisable,
                typeName: 'number'
              },
              component: rewardFix,
              show: this.fieldHasValue('rewardType', 'fix'),
              validate: (val) => {
                return [
                  this.validate(val).fractionDigitsMax(2)
                ]
              }
            },

            cashBackPeriod: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.cashbackPeriod'),
                disabled: this.forceDisable,
                typeName: 'number'
              },
              component: vInput,
              validate: (val) => {
                return [
                  this.validate(val).min(3),
                  this.validate(val).max(90)
                ]
              },
              show: () => true
            },

            online: {
              default: false,
              title: this.$i18n.t('components.Profile.formFields.howToGetCashback') + ((this.reqCountry.USA || this.reqCountry.MEXICO) ? '*' : ''),
              props: {
                label: this.$i18n.t('components.Profile.formFields.onlineStore'),
                type: 'boxed',
                val: 'online',
                disabled: this.forceDisable
              },
              // required: !this.hasFieldVal('offline') && this.reqCountry.USA,
              component: vCheckbox,
              validate: (val) => {
                return [
                  this.validateOnlineOffline(val)
                ]
              },
              show: () => true
            },

            offline: {
              default: false,
              props: {
                label: this.$i18n.t('components.Profile.formFields.retailStore'),
                type: 'boxed',
                val: 'offline',
                disabled: this.forceDisable
              },
              // required: !this.hasFieldVal('online') && this.reqCountry.USA,
              validate: (val) => {
                return [
                  this.validateOnlineOffline(val)
                ]
              },
              component: vCheckbox,
              show: () => true
            },

            checkAmountMax: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.checkAmountMax'),
                disabled: this.forceDisable,
                typeName: 'number'
              },
              // validate: (val) => {
              //   return [this.validate(val).minLength(2), this.validate(val).maxLength(250)]
              // },
              component: vInput,
              required: false
            },

            isNeedToConfirmQr: {
              title: this.$i18n.t('components.Profile.formFields.retailStoreSettings'),
              default: false,
              props: {
                label: this.$i18n.t('components.Profile.formFields.requireResponseQrCode'),
                type: 'boxed',
                val: 'isNeedToConfirmQr',
                disabled: this.fieldHasValue('offline', false) || this.forceDisable
              },
              component: vCheckbox,
              description: this.fieldHasValue('offline', false) ? this.$i18n.t('components.Profile.formFields.availableOnlyToRetailStore') : null
            },

            numberOfRetailCashbox: {
              default: null,
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.numberOfCheckoutsForRetailStore'),
                typeName: 'number',
                disabled: this.fieldHasValue('offline', false) || this.forceDisable
              },
              validate: (val) => {
                return this.validate(val).positive()
              },
              component: vInput,
              description: this.fieldHasValue('offline', false) ? this.$i18n.t('components.Profile.formFields.availableOnlyToRetailStore') : null,
              show: !this.reqCountry.USA && !this.reqCountry.MEXICO
            },

            checkScanLimit: {
              default: null,
              props: {
                label: this.$i18n.t('components.Profile.formFields.maxNumOfScannedReceipts'),
                placeholder: this.$i18n.t('components.Profile.formFields.maxNumOfScannedReceiptsPermonth'),
                typeName: 'number',
                disabled: this.fieldHasValue('offline', false) || this.forceDisable
              },
              validate: (val) => {
                return [this.validate(val).max(90), this.validate(val).min(1)]
              },
              component: vInput,
              description: this.fieldHasValue('offline', false) ? this.$i18n.t('components.Profile.formFields.availableOnlyToRetailStore') : null,
              show: !this.reqCountry.USA && !this.reqCountry.MEXICO
            },

            website: {
              title: this.$i18n.t('components.Profile.formFields.additionalInformation'),
              default: null,
              props: {
                placeholder: this.fieldHasValue('online', true) ? this.$i18n.t('components.Profile.formFields.onlineStoreAddress') + '*' : this.$i18n.t('components.Profile.formFields.onlineStoreAddress'),
                label: this.fieldHasValue('online', true) ? this.$i18n.t('components.Profile.formFields.onlineStoreAddressWebsite') + '*' : this.$i18n.t('components.Profile.formFields.onlineStoreAddressWebsite'),
                disabled: this.forceDisable
              },
              component: vInput,
              required: this.fieldHasValue('online', true)
            },

            countryList: {
              default: [],
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.countryList'),
                items: this.computedCountries,
                itemText: 'name',
                valueField: 'id',
                disabled: this.forceDisable
              },
              component: Multiselect,
              show: !this.reqCountry.USA && !this.reqCountry.MEXICO
            },

            cityList: {
              default: [],
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.cityList'),
                items: this.cities || [],
                itemText: 'name',
                valueField: 'id',
                disabled: this.forceDisable
              },
              component: Multiselect,
              show: !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            opportunityGoldShop: {
              title: this.$i18n.t('components.Profile.formFields.opportunityGoldshop'),
              default: false,
              props: {
                label: this.$i18n.t('components.Profile.formFields.agree'),
                type: 'boxed',
                val: 'opportunityGoldShop',
                disabled: this.forceDisable,
                reqCountry: this.reqCountry
              },
              component: this.reqCountry.USA || this.reqCountry.MEXICO ? goldShopConditions : vCheckbox
            }
          },
          disabled: (this.reqCountry.USA || this.reqCountry.MEXICO) ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry'),
          disabledDesc: () => {
            if ((this.reqCountry.USA || this.reqCountry.MEXICO) && this.hasFieldVal('reqCountry') && !this.hasFieldVal('rulesAccept') && !this.profileProp) {
              return this.$i18n.t('components.Profile.formFields.requiresAgreementStep1')
            }

            return this.$i18n.t('components.Profile.formFields.selectCountryOfRegistration')
          }
        },

        responsiblePersons: {
          name: (this.reqCountry.USA || this.reqCountry.MEXICO) ? this.$i18n.t('components.Profile.formFields.contactOfResponsiblePersons') + '*' : this.$i18n.t('components.Profile.formFields.contactOfResponsiblePersons'),
          fields: {
            contactTechnical: {
              default: null,
              props: {
                placeholder: (this.reqCountry.USA || this.reqCountry.MEXICO) ? this.$i18n.t('components.Profile.formFields.technicalIssues') + '*' : this.$i18n.t('components.Profile.formFields.technicalIssues'),
                disabled: this.forceDisable
              },
              component: vInput,
              required: (this.reqCountry.USA || this.reqCountry.MEXICO)
            },
            contactFinances: {
              default: null,
              props: {
                placeholder: (this.reqCountry.USA || this.reqCountry.MEXICO) ? this.$i18n.t('components.Profile.formFields.financeAndDocumentManagement') + '*' : this.$i18n.t('components.Profile.formFields.financeAndDocumentManagement'),
                disabled: this.forceDisable
              },
              component: vInput,
              required: (this.reqCountry.USA || this.reqCountry.MEXICO)
            },
            contactMarketingPrAd: {
              default: null,
              props: {
                placeholder: (this.reqCountry.USA || this.reqCountry.MEXICO) ? this.$i18n.t('components.Profile.formFields.marketingPrAndAdvertising') + '*' : this.$i18n.t('components.Profile.formFields.marketingPrAndAdvertising'),
                disabled: this.forceDisable
              },
              component: vInput,
              required: (this.reqCountry.USA || this.reqCountry.MEXICO)
            }
          },
          disabled: (this.reqCountry.USA || this.reqCountry.MEXICO) ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry'),
          disabledDesc: () => {
            if ((this.reqCountry.USA || this.reqCountry.MEXICO) && this.hasFieldVal('reqCountry') && !this.hasFieldVal('rulesAccept') && !this.profileProp) {
              return this.$i18n.t('components.Profile.formFields.requiresAgreementStep1')
            }

            return this.$i18n.t('components.Profile.formFields.selectCountryOfRegistration')
          }
        },

        marketingMaterialsSection: {
          name: this.$i18n.t('components.Profile.formFields.marketingMaterials'),
          description: this.$i18n.t('components.Profile.formFields.allowedFileFormat'),
          disabled: (this.reqCountry.USA || this.reqCountry.MEXICO) ? !this.hasFieldVal('reqCountry', 'rulesAccept') && !this.profileProp : !this.hasFieldVal('reqCountry'),
          disabledDesc: () => {
            if ((this.reqCountry.USA || this.reqCountry.MEXICO) && this.hasFieldVal('reqCountry') && !this.hasFieldVal('rulesAccept') && !this.profileProp) {
              return this.$i18n.t('components.Profile.formFields.requiresAgreementStep1')
            }

            return this.$i18n.t('components.Profile.formFields.selectCountryOfRegistration')
          },
          fields: {
            MM_LOGO: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.storeLogo'),
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => true
            },
            MM_BIG_PHOTO: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.bigPicture'),
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => true
            },
            marketingDescription: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.textAboutYourCompany'),
              description: this.$i18n.t('components.Profile.formFields.shortStoryAboutCompnay'),
              props: {
                placeholder: this.$i18n.t('components.Profile.formFields.tellUsAboutCompany'),
                label: this.$i18n.t('components.Profile.formFields.aboutCompany'),
                disabled: this.forceDisable
              },
              component: vTextarea,
              show: () => true
            }
          }
        },

        voucherSection: {
          name: this.$i18n.t('components.Profile.formFields.photoOfChecks'),
          description: this.$i18n.t('components.Profile.formFields.allowedFileFormat'),
          disabled: (this.reqCountry.USA || this.reqCountry.MEXICO) || !this.hasFieldVal('reqCountry') || this.fieldHasValue('offline', false),
          disabledDesc: () => {
            if (this.reqCountry.USA || this.reqCountry.MEXICO) {
              return this.$i18n.t('components.Profile.formFields.notAvailableInYourCountry')
            }

            if (!this.hasFieldVal('reqCountry')) {
              return this.$i18n.t('components.Profile.formFields.selectCountryOfRegistration')
            }

            if (this.fieldHasValue('offline', false)) {
              return this.$i18n.t('components.Profile.formFields.availableOnlyToRetailStore')
            }
          },
          fields: {
            CHECK_EXAMPLE_1: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.exampleOfCheckN') + ' 1',
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => true
            },
            CHECK_EXAMPLE_2: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.exampleOfCheckN') + ' 2',
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => true
            },
            CHECK_EXAMPLE_3: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.exampleOfCheckN') + ' 3',
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => true
            },
            CHECK_EXAMPLE_4: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.exampleOfCheckN') + ' 4',
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => true
            },
            CHECK_EXAMPLE_5: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.exampleOfCheckN') + ' 5',
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => true
            }
          }
        },

        shopSection: {
          name: this.$i18n.t('components.Profile.formFields.theShops') + (this.fieldHasValue('offline', true) ? '*' : ''),
          show: () => true,
          disabled: ((this.reqCountry.USA || this.reqCountry.MEXICO) && this.hasFieldVal('reqCountry') && !this.hasFieldVal('rulesAccept') && !this.profileProp) || !this.hasFieldVal('reqCountry') || this.fieldHasValue('offline', false),
          disabledDesc: () => {
            if ((this.reqCountry.USA || this.reqCountry.MEXICO) && this.hasFieldVal('reqCountry') && !this.hasFieldVal('rulesAccept') && !this.profileProp) {
              return this.$i18n.t('components.Profile.formFields.requiresAgreementStep1')
            }

            if (!this.hasFieldVal('reqCountry')) {
              return this.$i18n.t('components.Profile.formFields.selectCountryOfRegistration')
            }

            if (this.fieldHasValue('offline', false)) {
              return this.$i18n.t('components.Profile.formFields.availableOnlyToRetailStore')
            }
          },
          fields: {
            shopList: {
              default: [
                {
                  name: null,
                  address: null,
                  lat: null,
                  lng: null
                }
              ],
              props: {
                disabled: this.forceDisable
              },
              required: this.fieldHasValue('offline', true),
              validate: (val) => {
                return [
                  this.validateShopListMap(val)
                ]
              },
              component: addShop
            }
          }
        },

        documentSection: {
          name: (this.reqCountry.USA || this.reqCountry.MEXICO) && this.newProfile ? this.$i18n.t('components.Profile.formFields.theDocuments') + '*' : this.$i18n.t('components.Profile.formFields.theDocuments'),
          description: (this.reqCountry.USA) ? null : this.$i18n.t('components.Profile.formFields.allowedFileFormat'),
          disabled: (!this.newProfile && (this.reqCountry.USA)) || ((this.reqCountry.USA || this.reqCountry.MEXICO) && this.hasFieldVal('reqCountry') && !this.hasFieldVal('rulesAccept') && !this.profileProp) || !this.hasFieldVal('reqCountry'),
          disabledDesc: () => {
            if ((this.reqCountry.USA || this.reqCountry.MEXICO) && this.hasFieldVal('reqCountry') && !this.hasFieldVal('rulesAccept') && !this.profileProp) {
              return this.$i18n.t('components.Profile.formFields.requiresAgreementStep1')
            }

            if (!this.hasFieldVal('reqCountry')) {
              return this.$i18n.t('components.Profile.formFields.selectCountryOfRegistration')
            }
          },
          fields: {
            TAXPAYER_IDENTITY_CARD: {
              default: null,
              title: this.$i18n.t('components.Profile.taxPayerIdentificationNo') + '*',
              required: this.reqCountry.MEXICO,
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => this.reqCountry.MEXICO
            },
            documentsAccept: {
              default: !this.newProfile,
              component: documentsAccept,
              required: true,
              props: {
                disabled: this.forceDisable || !this.newProfile,
                reqCountry: this.reqCountry
              },
              show: () => this.reqCountry.MEXICO || this.reqCountry.USA
            },
            documentType: {
              default: 'org',
              component: documentTypes,
              optionalDesc: false,
              show: (!this.reqCountry.USA && !this.reqCountry.MEXICO)
            },
            IP_SVIDETELSTVO_O_REGISTRACII: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.ipRegistrationCertificate'),
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => this.fieldHasValue('documentType', 'ip') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            IP_SVIDETELSTVO_O_REGISTRACII_NALOG: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.certificateOfRegistrationWithTaxAuthority'),
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => this.fieldHasValue('documentType', 'ip') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },

            OOO_USTAV: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.copyOfCharter'),
              component: UploadSingle,
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              show: () => this.fieldHasValue('documentType', 'org') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            OOO_PRIKAZ_O_NAZNACHENII_RUKOVODITELEY: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.copyOfOrderOnAppointmentOfManagers'),
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => this.fieldHasValue('documentType', 'org') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            OOO_DOVERENNOST_NA_PODPIS: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.copyOfAttorneySecuring'),
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => this.fieldHasValue('documentType', 'org') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            OOO_SVIDETELSTVO_O_POSTANOVKE_NA_UCHET_NALOG: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.copyOfCertificateOfRegistrationWithTaxAuthority'),
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => this.fieldHasValue('documentType', 'org') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            },
            OOO_SVIDETELSTVO_O_GOS_REGISTRASII_UL: {
              default: null,
              title: this.$i18n.t('components.Profile.formFields.copyOfCertificateOfStateRegistrationOfLegalEntity'),
              props: {
                disabled: this.forceDisable,
                clientOnly: true,
                fileExtensions: fileExtensions
              },
              component: UploadSingle,
              show: () => this.fieldHasValue('documentType', 'org') && !this.reqCountry.USA && !this.reqCountry.MEXICO
            }
          }
        }
      }
    }
  }
}

// uploadFileMultiple: {
//   default: [],
//   title: 'Загрузить несколько файлов',
//   description: '',
//   props: {
//     placeholder: 'Файл'
//   },
//   component: () => import('@/components/controls/UploadMultiple/'),
//   show: () => true
// },
